import { FuturePayment } from '@app/models/FuturePayment';
import { PaymentLineItem } from '@app/models/PaymentLineItem';
import { Property } from '@app/models/Property';
import { PaymentApi } from '@app/services/apiServices/PaymentApi';
import { PropertyApi } from '@app/services/apiServices/PropertyApi';
import { PaymentEditStore } from '@app/services/stores/PaymentEditStore';
import { AppDate } from '@app/util/app-date';
import { PaymentMethod } from '@app/models/PaymentMethod';
import { PaymentSchedule } from '@app/models/PaymentSchedule';
import { PaymentMethodApi } from '@app/services/apiServices/PaymentMethodApi';
import { PaymentScheduleApi } from '@app/services/apiServices/PaymentScheduleApi';
import { ErrorHandlerService } from '@app/services/ErrorHandler.service';
import { NetworkErrorService } from "@app/services/NetworkError.service";
import { SpinnerService } from '@app/services/spinner.service';
import { UserStore } from '@app/services/stores/UserStore';
import { moneyAdd, moneyNumber } from "@app/util/money-number";
import { catchError, concatMap, finalize, switchMap, take, takeUntil, tap } from "rxjs/operators";
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from '@angular/material/dialog';
import { FeeChangeWarningDialog } from "@app/pages/payment/scheduleEdit/feeChangeWarningDialog";
import { ScheduleViewDeleteDialog } from "@app/pages/payment/scheduleView/scheduleViewDialogDelete";
import { AppDataService } from "@app/services/appData.service";
import { ActivatedRoute, Router } from "@angular/router";
import { PaymentTimingType } from '@app/services/shared/PaymentTimingType.enum';
import { ScheduleViewCancelDialog } from '../scheduleView/scheduleViewCancel.dialog';
import { OneButtonDialogComponent } from '@app/components/sharedDialogs/oneButtonDialog.component';
import { filter, Observable, of, Subject } from "rxjs";
import { DATE_PICKER_CONFIG } from "@app/components/material/matDatePicker/mat-date-picker.component";
import { PaymentUiState } from '@app/services/shared/PaymentUiState.enum';
import { ValidPaymentStatusType } from '@app/services/shared/ValidPaymentStatusType';
import { AdminFeeResponse } from "@app/models/AccountBalanceAdminResponse";

const StatusType: any = {
    ACCOUNT_BALANCE: 'account-balance',
    FIXED_AMOUNT: 'fixed-amount'
};

@Component({
    selector: 'app-schedule-edit',
    template: require('./scheduleEdit.component.html'),
})
export class ScheduleEditComponent implements OnInit, OnDestroy {

    paymentScheduleId: number;
    paymentSchedule: Readonly<PaymentSchedule>;
    paymentMethods: PaymentMethod[];
    paymentMethodError: boolean;
    paymentMethodErrorMsg: string;

    property: Property;

    originalConvenienceFee: number;
    originalPaymentMethod: PaymentMethod;

    currentConvenienceFee: number;
    currentPaymentMethod: PaymentMethod;
    currentNickname: string;

    scheduleNicknameError: boolean;
    ACHMessage: string;
    cardMessage: string;

    amountToPay: number;
    originalAmountToPay: number;
    amountToPayErrorMsg: string;
    calculatingConvenienceFee: boolean = true;
    totalAmount: number;
    tcCheckbox: boolean = false;

    allowPayByCheck: boolean = true;
    allowPayByCard: boolean = true;

    fixedDay: number;
    originalFixedDay: number;

    maxPrincipalAmount: number;
    accountBalanceTbd: boolean = true;
    daysOfMonth: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31];
    prevFormattedStartDate: string;
    timer: number;
    currentFeeType: string;
    originalFeeRate: number; // account-balance only
    originalFeeType: string;
    sortedFuturePayments: FuturePayment[];

    private destroy$ = new Subject<void>();

    /** config for date-picker, must be set in constructor! */
    paymentDatePickerConfig: DATE_PICKER_CONFIG = null;

    uiManipulation: PaymentUiState;

    constructor(
        @Inject(MatDialog) private dialog: any,
        @Inject(SpinnerService) private spinnerService: SpinnerService,
        @Inject(PaymentScheduleApi) private paymentScheduleApi: PaymentScheduleApi,
        @Inject(PaymentEditStore) private paymentEditStore: PaymentEditStore,
        @Inject(PaymentApi) private paymentApi: PaymentApi,
        @Inject(NetworkErrorService) private networkErrorService: NetworkErrorService,
        @Inject(ErrorHandlerService) private errorHandlerService: ErrorHandlerService,
        @Inject(PaymentMethodApi) private paymentMethodApi: PaymentMethodApi,
        @Inject(PropertyApi) private propertyApi: PropertyApi,
        @Inject(UserStore) private userStore: UserStore,
        @Inject(AppDataService) private appDataService: AppDataService,
        @Inject(Router) private router: Router,
        @Inject(ActivatedRoute) private route: ActivatedRoute) {

    }

    ngOnInit() {

        this.ACHMessage = "Please note: you are making a payment through First Citizens Bank’s proprietary ACH payment system.";

        if (this.route.snapshot.params.scheduleId) {
            this.paymentScheduleId = this.route.snapshot.params.scheduleId;
        } else if (!window.__karma__) {
            console.error("No paymentScheduleId");
        }

        this.spinnerService.showSpinner();

        this.refreshData()
        this.maxPrincipalAmount = this.appDataService.appSettings.paymentLimits.maxPrincipalAmount;
        this.getPaymentSchedule().pipe(
            takeUntil(this.destroy$),
            finalize(() => this.spinnerService.closeSpinner())
        ).subscribe();
    }

    setAllowCheckAndCard(allowPayByCheck: boolean, allowPayByCard: boolean) {
        this.allowPayByCheck = allowPayByCheck;
        this.allowPayByCard = allowPayByCard;
    }

    filterAllowedPaymentMethods(paymentMethods: PaymentMethod[]): PaymentMethod[] {
        return paymentMethods.filter(paymentMethod => {
            if (paymentMethod.type === 'card')
                return this.allowPayByCard
            else if (paymentMethod.type === 'ach')
                return this.allowPayByCheck
            else
                return true;
        });
    }

    getPaymentSchedule(): Observable<Property> {
        return this.paymentScheduleApi.paymentSchedulesIdGet(this.paymentScheduleId).pipe(
            tap(resp => {
                this.paymentSchedule = resp;
                this.createSortedFuturePayments();
                this.prevFormattedStartDate = this.sortedFuturePayments?.[0]?.date?.formattedFullDate;
            }),
            concatMap(() => this.propertyApi.propertiesIdGet(this.paymentSchedule.propertyId)),
            tap(resp => {
                this.property = resp
            })
        );
    }

    paymentDateHelper() {
        this.dialog.open(OneButtonDialogComponent, {
            data: {
                "header": "Start Date",
                "message": "These are the available start dates based on your Management Company's payment cycle. Payments are processed Monday through Friday, excluding holidays.",
                "buttonText": "Close"
            }
        })
    }

    createSortedFuturePayments() {
        this.sortedFuturePayments = [];

        if (this.paymentSchedule &&
            this.paymentSchedule.futurePayments &&
            this.paymentSchedule.futurePayments.length > 0) {

            this.sortedFuturePayments = this.paymentSchedule.futurePayments
                .filter(a => a.status == 'scheduled')
                .sort((a, b) => {

                    let yearResult = a.date.year - b.date.year;
                    if (yearResult !== 0) {
                        return yearResult;
                    }

                    let monthResult = a.date.month - b.date.month;
                    if (monthResult !== 0) {
                        return monthResult;
                    }

                    let dayResult = a.date.day - b.date.day;
                    if (dayResult !== 0) {
                        return dayResult;
                    }

                    return a.id - b.id;
                });
        }
    }

    disableCancelNextPayment(): boolean {
        if (this.sortedFuturePayments?.length > 1) {
            return false;
        }
        return true;
    }

    cancelPayment() {
        let fp = JSON.parse((JSON.stringify(this.sortedFuturePayments[0])));
        let dialogRef = this.dialog.open(ScheduleViewCancelDialog, {
            data: {
                paymentDate: fp.date.formattedFullDate,
                paymentAmount: fp.total
            }
        })

        dialogRef.afterClosed().pipe(
            take(1),
            takeUntil(this.destroy$),
            filter(value => value === 'Cancel'),
            tap(() => {
                fp.status = 'cancelled-by-user';
                this.spinnerService.showSpinner();
            }),
            concatMap(() => this.paymentScheduleApi.futurePaymentsPost(fp)),
            concatMap(() => this.getPaymentSchedule()),
            finalize(() => this.spinnerService.closeSpinner()),
            concatMap(() => {
                return this.dialog.open(OneButtonDialogComponent, {
                    disableClose: true,
                    data: {
                        header: 'Your Next Payment Is Cancelled',
                        message: `Your next payment date is ${this.prevFormattedStartDate}`,
                        buttonText: 'OK'
                    }
                }).afterClosed().pipe(take(1));
            })
        ).subscribe({
            next: () => {
                this.router.navigateByUrl('/properties');
            },
            error: (err) => {
                console.debug('error changing future payment ' + fp.id + ' status to cancelled');
                if (err?.status === 400) {
                    this.dialog.open(OneButtonDialogComponent, {
                        data: {
                            header: 'Cancel Payment',
                            message: err.error.message,
                            buttonText: 'OK'
                        }
                    });
                } else {
                    this.networkErrorService.handleNetworkError(err, true);
                }

            }
        });
    }

    refreshData() {

        try {
            this.paymentScheduleApi.paymentSchedulesIdGet(this.paymentScheduleId)
                .pipe(take(1)).subscribe(scheduleResp => {
                    this.paymentSchedule = Object.freeze(scheduleResp);
                    this.originalConvenienceFee = this.paymentSchedule.feeAmount;
                    this.amountToPay = this.paymentSchedule.principalAmount;
                    this.originalAmountToPay = this.amountToPay;
                    this.currentNickname = this.paymentSchedule.nickname;
                    this.fixedDay = this.paymentSchedule.fixedDay ? this.paymentSchedule.fixedDay : this.paymentSchedule.startDate.day;
                    this.originalFixedDay = this.fixedDay;
                    const propertyId = this.paymentSchedule.propertyId;
                    this.propertyApi.propertiesIdGet(propertyId)
                        .pipe(take(1)).subscribe({ next: propertyResp => {
                            this.property = propertyResp;
                            this.propertyApi.propertiesValidateGet(false, this.property.hoa.pmc.couponId, this.property.hoa.couponId, this.property.couponId)
                                .pipe(take(1)).subscribe({ next: (validateResp) => {
                                    this.setAllowCheckAndCard(validateResp.achPayments, validateResp.cardPayments);
                                    const userId = this.userStore.getHoaHomeownerId();
                                    this.paymentMethodApi.homeownersIdPaymentMethodsGet(userId)
                                        .pipe(take(1)).subscribe({ next: (pmtMethodResp) => {
                                            this.paymentMethods = this.filterAllowedPaymentMethods(pmtMethodResp);
                                            const schedulePmtMethodId = Number(this.paymentSchedule.paymentMethodId);
                                            this.paymentMethods
                                                .filter(method => Number(method.id) === schedulePmtMethodId)
                                                .slice(0, 1)
                                                .forEach(method => {
                                                    this.currentPaymentMethod = method;
                                                    this.originalPaymentMethod = method;
                                                    this.getConvenienceFee(this.originalPaymentMethod).then(fee => {
                                                        if (this.paymentSchedule.type === 'account-balance') {
                                                            // account-balance: balance is unknown, fee/total persist as 0;
                                                            this.originalFeeRate = fee;
                                                            this.originalFeeType = this.currentFeeType;
                                                            this.currentConvenienceFee = 0;
                                                            this.totalAmount = +this.amountToPay;
                                                        } else {
                                                            this.currentConvenienceFee = fee;
                                                            this.totalAmount = +this.amountToPay + this.currentConvenienceFee;
                                                        }
                                                        this.calculatingConvenienceFee = false;
                                                        this.spinnerService.closeSpinner();
                                                    })
                                                });
                                        },
                                        error:reason => {
                                            this.networkErrorService.handleNetworkError(reason, true)
                                                .pipe(take(1)).subscribe(shouldRetry => {
                                                    if (shouldRetry === true) {
                                                        this.refreshData()
                                                    }
                                                })
                                        } }
                                        );
                                },
                                error: reason => {
                                    this.networkErrorService.handleNetworkError(reason, true)
                                        .pipe(take(1)).subscribe(shouldRetry => {
                                            if (shouldRetry === true) {
                                                this.refreshData()
                                            }
                                        })
                                } }
                                )
                        },
                        error: reason => {
                            this.networkErrorService.handleNetworkError(reason, true)
                                .pipe(take(1)).subscribe(shouldRetry => {
                                    if (shouldRetry === true) {
                                        this.refreshData()
                                    }
                                })
                        } }
                        );

                });
        }
        catch(reason) {
            this.networkErrorService.handleNetworkError(reason, true)
                .pipe(take(1)).subscribe(shouldRetry => {
                    if (shouldRetry === true) {
                        this.refreshData()
                    }
                })
        }
    }

    changeFixedDay () {
        this.spinnerService.showSpinner();

        this.loadPaymentScheduleData().pipe(
            takeUntil(this.destroy$),
            catchError(err =>
                this.networkErrorService.handleNetworkError(err, true).pipe(
                    takeUntil(this.destroy$),
                    filter(shouldRetry => shouldRetry === true),
                    switchMap(() => this.loadPaymentScheduleData())
                )
            ),
            finalize(() => this.spinnerService.closeSpinner())
        ).subscribe();
    }

    private loadPaymentScheduleData(): Observable<Property> {
        return this.paymentScheduleApi.paymentScheduleChangeFixedDayGet(this.paymentScheduleId, this.fixedDay).pipe(
            tap(scheduleResp => {
                this.paymentSchedule = scheduleResp;
                this.createSortedFuturePayments();
            }),
            concatMap(() => this.propertyApi.propertiesIdGet(this.paymentSchedule.propertyId)),
            tap(propertyResp => {
                this.property = propertyResp;
            })
        );
    }

    /** convenience fee message should be based on if the convenience fee changes */
    getConvenienceFee(pmtMethod: PaymentMethod): Promise<number> {
        const amount = this.paymentSchedule.principalAmount ? this.paymentSchedule.principalAmount : 0;
        const pmtMethodState = pmtMethod.state || this.property.address.state;
        const paymentType: 'ACH' | 'CARD' =  pmtMethod.routingNumber ? "ACH" : "CARD";
        return new Promise<number>((resolve, reject) => {
            if (this.paymentSchedule.type === 'account-balance') {
                this.paymentApi
                    .getAdminFee(
                        this.property.hoa.pmc.couponId,
                        this.property.hoa.couponId,
                        paymentType,
                        PaymentTimingType.SCHEDULED,
                        this.paymentSchedule.frequency.toUpperCase()
                    )
                    .pipe(take(1))
                    .subscribe({
                        next: (resp: AdminFeeResponse) => {
                            this.currentFeeType = resp.type;
                            resolve(resp.amount)
                        },
                        error: err => reject(err)
                    });
            } else {
                this.paymentApi
                    .convenienceFeePropertyIdAmountGet(
                        false,
                        this.property.address.state,
                        pmtMethodState,
                        amount,
                        this.property.hoa.pmc.couponId,
                        this.property.hoa.couponId,
                        paymentType,
                        PaymentTimingType.SCHEDULED,
                        this.paymentSchedule.frequency
                    )
                    .pipe(take(1))
                    .subscribe({
                        next: (resp) => resolve(resp.convenienceFee),
                        error: err => reject(err),
                    });
            }
        });
    }

    scheduleNicknameChange() {
        this.scheduleNicknameError = !this.currentNickname || this.currentNickname.trim().length === 0;
    }

    scheduleNicknameBlur() {
        clearTimeout(this.timer);
        // @ts-ignore
        this.timer = setTimeout(() => {
            this.scheduleNicknameChange();
        },1000)
    }

    onChangePmtMethod() {
        this.calculatingConvenienceFee = true;

        this.getConvenienceFee(this.currentPaymentMethod).then(newFee => {
            const isAccountBalance = this.paymentSchedule.type === 'account-balance';

            const baselineFee = isAccountBalance
                ? this.originalFeeRate
                : this.originalConvenienceFee;

            const oldFeeAmount = Number(baselineFee);
            const newFeeAmount = Number(newFee);

            const isAccountBalanceNoFeeToNoFee =
                isAccountBalance &&
                oldFeeAmount === 0 &&
                newFeeAmount === 0;

            const feeChanged = isAccountBalance
                ? !isAccountBalanceNoFeeToNoFee &&
                (
                    this.originalFeeType !== this.currentFeeType ||
                    oldFeeAmount !== newFeeAmount
                )
                : Number(this.originalConvenienceFee) !== newFeeAmount;

            const paymentMethodChanged =
                Number(this.originalPaymentMethod.id) !== Number(this.currentPaymentMethod.id);

            const shouldShowPaymentMethodChangedDialog =
                !isAccountBalanceNoFeeToNoFee &&
                (paymentMethodChanged || feeChanged);

            if (!isAccountBalance) {
                this.currentConvenienceFee = newFee;
            }

            if (shouldShowPaymentMethodChangedDialog) {
                const dialogRef = this.dialog.open(FeeChangeWarningDialog, {
                    disableClose: true,
                    data: {
                        oldPaymentMethod: `${this.originalPaymentMethod.nickname} (${this.originalPaymentMethod.maskedId})`,
                        newPaymentMethod: `${this.currentPaymentMethod.nickname} (${this.currentPaymentMethod.maskedId})`,
                        oldFee: baselineFee,
                        newFee: newFee,

                        oldIsPercentage: isAccountBalance && this.originalFeeType === 'percent',
                        newIsPercentage: isAccountBalance && this.currentFeeType === 'percent',

                        isAccountBalance: isAccountBalance,
                        feeChanged: feeChanged
                    },
                });

                dialogRef.afterClosed().pipe(take(1)).subscribe(value => {
                    if (value === 'OK') {
                        this.updateAmount();
                    } else if (value === 'Cancel') {
                        this.currentPaymentMethod = this.originalPaymentMethod;
                        this.currentConvenienceFee = this.originalConvenienceFee;

                        if (isAccountBalance) {
                            this.currentFeeType = this.originalFeeType;
                        }
                    } else {
                        this.currentPaymentMethod = this.originalPaymentMethod;
                        this.currentConvenienceFee = this.originalConvenienceFee;

                        if (isAccountBalance) {
                            this.currentFeeType = this.originalFeeType;
                        }
                    }

                    this.calculatingConvenienceFee = false;
                }, () => {
                    this.calculatingConvenienceFee = false;
                });
            } else {
                this.updateAmount();
                this.calculatingConvenienceFee = false;
            }
        })
    }

    save() {
        let updatedSchedule: PaymentSchedule;
        if ((this.currentConvenienceFee !== this.originalConvenienceFee) || (+this.amountToPay !== this.originalAmountToPay)) {
            // update convenience fees
            updatedSchedule = {
                ...this.paymentSchedule, // using spread we don't have to worry about mutations
                acceptedAmountsDate: AppDate.now().format("yyyy-MM-dd"),
                futurePayments: updateScheduleFeeItems(this.paymentSchedule, this.currentConvenienceFee, +this.amountToPay),
                paymentMethod: this.currentPaymentMethod,
                paymentMethodId: this.currentPaymentMethod.id,
                nickname: this.currentNickname,
                remainingPayments: (this.paymentSchedule.remainingPayments && this.paymentSchedule.endDate) ? this.paymentSchedule.remainingPayments : null,
                endDate: (this.paymentSchedule.remainingPayments && this.paymentSchedule.endDate) ? this.paymentSchedule.endDate : null,
                feeAmount: this.currentConvenienceFee,
                paymentMethodType: this.currentPaymentMethod.type,
                principalAmount: +this.amountToPay,
                totalAmount: moneyNumber(this.totalAmount)
            }
        } else {
            updatedSchedule = {
                ...this.paymentSchedule,
                paymentMethod: this.currentPaymentMethod,
                paymentMethodId: this.currentPaymentMethod.id,
                nickname: this.currentNickname,
                remainingPayments: (this.paymentSchedule.remainingPayments && this.paymentSchedule.endDate) ? this.paymentSchedule.remainingPayments : null,
                endDate: (this.paymentSchedule.remainingPayments && this.paymentSchedule.endDate) ? this.paymentSchedule.endDate : null,
                paymentMethodType: this.currentPaymentMethod.type
            }
        }

        this.paymentSchedulesUpdate(updatedSchedule);
    }

    private paymentSchedulesUpdate (updateSchedule: PaymentSchedule) {
        const dayChanged = this.fixedDay !== this.originalFixedDay;
        const confirm$: Observable<void> = dayChanged ? this.showPaymentDateDialog() : of(undefined);

        confirm$.pipe(
            take(1),
            takeUntil(this.destroy$),
            tap(() => this.spinnerService.showSpinner()),
            concatMap(() => this.paymentScheduleApi.paymentSchedulesUpdatePost(
                updateSchedule
            )
            ),
            finalize(() => this.spinnerService.closeSpinner())
        ).subscribe({
            next: () => {
                this.router.navigateByUrl('/properties', { state: { scheduleId: this.paymentScheduleId } } )
            },
            error: (err) => {
                const errMsg = err?.error?.message || undefined;
                this.errorHandlerService.handleNetworkError(err, true, errMsg)
                    .pipe(take(1)).subscribe();
            }
        })
    }

    private showPaymentDateDialog(): Observable<void> {
        const nextPaymentDate = this.sortedFuturePayments?.[0]?.date?.formattedFullDate;
        let message: string;
        let message2: string;

        if (this.prevFormattedStartDate === nextPaymentDate) {
            message = `Your payment day of the month has been updated to ${this.fixedDay}.`
            message2 = `Your next payment date will remain as ${nextPaymentDate} due to a non business day.`
        } else {
            message = `Your next payment date has changed from ${this.prevFormattedStartDate} to ${nextPaymentDate}`
        }
        return this.dialog.open(OneButtonDialogComponent, {
            disableClose: true,
            data: {
                header: 'Confirm New Payment Date',
                message: message,
                message2: message2,
                buttonText: 'OK'
            }
        }).afterClosed();
    }


    isDueAmountInvalid(due) {
        return (!due || due <= 0 || this.amountToPayErrorMsg || isNaN(due));
    }

    amountToPayError(amtToPay) {
        if (this.maxPrincipalAmount && amtToPay > this.maxPrincipalAmount) {
            this.amountToPayErrorMsg = 'Payment amount cannot exceed $' + this.maxPrincipalAmount;
        } else {
            this.amountToPayErrorMsg = undefined;
        }
    }

    submitDisabled() {
        let accountBalance = this.paymentSchedule && this.paymentSchedule.type === 'account-balance';
        let zeroAmountDetected = !accountBalance && (this.amountToPay === 0 || this.amountToPay === null || this.amountToPay === undefined);

        return (this.currentPaymentMethod == null
            || (this.paymentSchedule == null || this.currentNickname == null || this.currentNickname.length === 0)
            || zeroAmountDetected
            || this.amountToPayErrorMsg
            || this.calculatingConvenienceFee
            || (this.paymentMethods && this.paymentMethods.length === 0)
            || !(this.currentPaymentMethod)
            || !this.tcCheckbox
            || this.scheduleNicknameError
        );
    }

    updateAmount() {
        this.amountToPay = moneyNumber(this.amountToPay);
        this.amountToPayError(+this.amountToPay);
        this.paymentEditStore.setAmountToPay(this.amountToPay || null);
        this.updateConvenienceFee();
    }

    updateConvenienceFee() {
        let paymentMethodType: "ACH" | "CARD";
        if (this.currentPaymentMethod) {
            if (this.currentPaymentMethod.routingNumber !== null && this.currentPaymentMethod.routingNumber !== undefined)
                paymentMethodType = "ACH";
            else
                paymentMethodType = "CARD";
        }

        if (this.property && this.currentPaymentMethod && +this.amountToPay > 0) {
            this.calculatingConvenienceFee = true;
            // ACH Payment methods do not currently set their paymentMethod.state
            const billingState = this.currentPaymentMethod.state || this.property.address.state;

            this.paymentApi.convenienceFeePropertyIdAmountGet(false, this.property.address.state, billingState,
                this.amountToPay, this.property.hoa.pmc.couponId, this.property.hoa.couponId, paymentMethodType, PaymentTimingType.SCHEDULED, this.paymentSchedule.frequency)
                .pipe(take(1)).subscribe({ next: (data) => {
                    this.calculatingConvenienceFee = false;
                    this.currentConvenienceFee = data.convenienceFee;
                    this.totalAmount = +this.amountToPay + this.currentConvenienceFee;
                }, error: () => {
                    this.calculatingConvenienceFee = false;
                } });
        } else {
            this.currentConvenienceFee = 0;
            this.totalAmount = 0;
        }
    }

    clearDue() {
        this.amountToPay = 0.00;
        this.updateAmount();
    }

    clearAmountFieldWhen0(due) {
        if (due <= 0) {
            due = null;
            this.updateAmount();
        }
    }

    removeSchedule() {

        const dialogRef = this.dialog.open(ScheduleViewDeleteDialog, {
            disableClose: true,
            data: {
                // accessed via ngDialogData.nickname in the template
                nickname: this.paymentSchedule && this.paymentSchedule.nickname
            }
        });
        dialogRef.afterClosed().pipe(
            take(1),
            takeUntil(this.destroy$),
            filter(value => value === 'Delete'),
            tap(() => this.spinnerService.showSpinner()),
            concatMap(() => this.paymentScheduleApi.paymentSchedulesIdDelete(this.paymentSchedule.id)),
            finalize(() => this.spinnerService.closeSpinner()),
            concatMap(() => {
                return this.dialog.open(OneButtonDialogComponent, {
                    disableClose: true,
                    data: {
                        header: 'Payment Schedule Canceled',
                        message:  'Your payment schedule has been canceled',
                        buttonText: 'OK'
                    }
                }).afterClosed().pipe(take(1));
            })
        ).subscribe({
            next: () => {
                this.router.navigateByUrl('/properties');
            },
            error: (err) => {
                const errMsg = err?.error?.message || undefined;
                this.errorHandlerService.handleNetworkError(err, true, errMsg)
                    .pipe(take(1)).subscribe();
            }
        })
    }

    cancel() {
        this.router.navigateByUrl('/properties', { state: { scheduleId: this.paymentScheduleId } });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}

/**
 * This is broken out into a function to make sure it's pure,
 * which makes it easier to reason about.
 */
export function updateScheduleFeeItems(sched: PaymentSchedule, newFee: number, newAmount: number): FuturePayment[] {
    return sched.futurePayments.map(futurePmt => {
        /** add up item amounts for updating total */
        let newTotal = 0;
        let updatedItems: PaymentLineItem[] = futurePmt.items.map(item => {
            // update each item
            if (/fee/i.test(item.description)) {
                newTotal = moneyAdd(newTotal, newFee);
                return {
                    ...item,
                    amount: newFee
                }
            } else {
                newTotal = moneyAdd(newTotal, newAmount)
                return {
                    ...item,
                    amount: newAmount
                }
            }
        });
        return {
            ...futurePmt,
            total: moneyNumber(newTotal),
            items: updatedItems,
        }
    })
}
